import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faDownload,
  faChevronLeft,
  faChevronRight,
  faFileLines,
} from "@fortawesome/free-solid-svg-icons";
import { Card, Table, Badge, InputGroup, Form, Button, Col, Row } from "@themesberg/react-bootstrap";
import { downloadUserCreditNoteById, userCreditNotesList } from "../../../pages/api";

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const capitalize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());


      const getStatusBadgeStyle = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "pending":
    case "inactive":
      return { background: "#f59e0b", color: "#ffffff" };
    case "success":
    case "completed":
    case "active":
    case "issued":
      return { background: "#10b981", color: "#ffffff" };
    case "failed":
    case "blocked":
    case "cancelled":
      return { background: "#ef4444", color: "#ffffff" };
    case "refunded":
            return { background: "#6366f1", color: "#ffffff" };
    default:
      return { background: "#6b7280", color: "#ffffff" };
  }
};

export default function UserCreditNotes() {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [creditNotes, setCreditNotes] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const searchDebounceRef = useRef(null);

  const fetchCreditNotes = async (page = 1, perPage = 10, searchTerm = "", from = "", to = "") => {
    try {
      setLoading(true);
      const response = await userCreditNotesList({
        page,
        per_page: perPage,
        search: searchTerm,
        date_from: from || undefined,
        date_to: to || undefined,
      });

      const apiData = response?.data?.data || {};
      setCreditNotes(Array.isArray(apiData.credit_notes) ? apiData.credit_notes : []);
      setPagination({
        current_page: Number(apiData?.pagination?.current_page ?? page) || page,
        last_page: Number(apiData?.pagination?.last_page ?? 1) || 1,
        per_page: Number(apiData?.pagination?.per_page ?? perPage) || perPage,
        total: Number(apiData?.pagination?.total ?? 0) || 0,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to fetch credit notes.");
      setCreditNotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCreditNotes(1, pagination.per_page, search, dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchCreditNotes(1, pagination.per_page, search, dateFrom, dateTo);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dateFrom, dateTo]);

  const handleDownload = async (creditNoteId, creditNoteNumber) => {
    try {
      const response = await downloadUserCreditNoteById(creditNoteId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${creditNoteNumber || `credit-note-${creditNoteId}`}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Credit note downloaded.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to download credit note.");
    }
  };

  const getShowingRange = () => {
    const total = pagination.total;
    const current = pagination.current_page;
    const perPage = pagination.per_page;

    if (!total) return { start: 0, end: 0 };

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page;
    const current = pagination.current_page;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

  return (
    <div className="lsg-page">
      <div className="d-flex justify-content-between flex-wrap gap-3 py-2 mb-3">
        <div>
          <h2 className="lsg-page-title">Credit Notes</h2>
          <p className="lsg-page-subtitle">
            Issued after enrollment finalization when opt-out is confirmed.
          </p>
        </div>
        <InputGroup style={{ maxWidth: "280px" }}>
          <InputGroup.Text className="bg-white">
            <FontAwesomeIcon icon={faSearch} className="text-muted" />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
      </div>

      <Card className="border-0 shadow-sm rounded-4 mb-3 lsg-card">
        <Card.Body className="p-3">
          <Row className="g-3">
            {/* From Date */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="small mb-1">From</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  className="w-100"
                />
              </Form.Group>
            </Col>

            {/* To Date */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="small mb-1">To</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  className="w-100"
                />
              </Form.Group>
            </Col>

            {/* <Button
              variant="outline-primary"
              className="reset-filter-btn"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
                setSearch("");
              }}
            >
              Reset
            </Button> */}
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm rounded-4 lsg-card">
        <Card.Body className="p-4">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0 lsg-table">
              <thead>
                <tr>
                  <th>Credit Note #</th>
                  <th>Date</th>
                  <th>Course</th>
                  <th>Invoice #</th>
                  <th>Base</th>
                  <th>GST</th>
                  <th>Total</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-5">Loading credit notes...</td>
                  </tr>
                ) : creditNotes.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-5">
                      <FontAwesomeIcon icon={faFileLines} className="mb-2" size="lg" />
                      <div>No credit notes found yet.</div>
                    </td>
                  </tr>
                ) : (
                  creditNotes.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-semibold">{item.credit_note_number}</td>
                      <td>{formatDate(item.credit_note_date)}</td>
                      <td>{capitalize(item.course_name)}</td>
                      <td>{item.invoice_number || "—"}</td>
                      <td>{formatCurrency(item.base_amount)}</td>
                      <td>{formatCurrency(item.gst_amount)}</td>
                      <td className="fw-semibold">{formatCurrency(item.total_amount)}</td>
                      <td>{capitalize(item.reason)}</td>
                      {/* <td>
                        <Badge bg="warning" text="dark">{capitalize(item.status)}</Badge>
                      </td> */}
                      <td>
                        <Badge className="px-3 py-2 rounded-pill" style={getStatusBadgeStyle(item.status)}>{capitalize(item.status)}</Badge>
                      </td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          className="act-btn"
                          onClick={() => handleDownload(item.id, item.credit_note_number)}
                        >
                          <FontAwesomeIcon icon={faDownload} className="me-1" />
                          PDF
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loading}
                onClick={() =>
                  fetchCreditNotes(
                    pagination.current_page - 1,
                    pagination.per_page,
                    search.trim(),
                    dateFrom,
                    dateTo
                  )
                }
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {getPageItems().map((p, i) =>
                p === "..." ? (
                  <span key={i} className="px-2">
                    ...
                  </span>
                ) : (
                  <Button
                    key={p}
                    variant="outline-primary"
                    disabled={loading}
                    className={`course-page-btn ${p === pagination.current_page
                      ? "course-page-btn-active"
                      : ""
                      }`}
                    onClick={() =>
                      fetchCreditNotes(
                        p,
                        pagination.per_page,
                        search.trim(),
                        dateFrom,
                        dateTo
                      )
                    }
                  >
                    {p}
                  </Button>
                )
              )}

              <Button
                variant="outline-primary"
                disabled={
                  pagination.current_page >= pagination.last_page || loading
                }
                onClick={() =>
                  fetchCreditNotes(
                    pagination.current_page + 1,
                    pagination.per_page,
                    search.trim(),
                    dateFrom,
                    dateTo
                  )
                }
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
